import React, { useEffect, useMemo, useState } from "react";
import {
  useOutletContext,
  useSearchParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import NavbarScroll from "../../components/subComponents/NavbarScroll";
import { useItemCategoryInfinite } from "../../hooks/useItemCategoryInfinite";
import ConnectCard2 from "../../components/subComponents/ConnectCard2";
import { saffronRout, saffronURL } from "../../Url";

const safeArray = (v) => (Array.isArray(v) ? v : []);
const addPosition = (arr) =>
  safeArray(arr).map((item, i) => ({ ...item, position: i }));

// ✅ Keep filters in URL while navigating
const keepKeys = [
  "country",
  "state",
  "city",
  "language",
  "category",
  "category_name",
];

const buildSafeSearch = (searchParams, sectionValue) => {
  const current = new URLSearchParams(searchParams);
  const next = new URLSearchParams();

  keepKeys.forEach((k) => {
    const v = current.get(k);
    if (v) next.set(k, v);
  });

  // ✅ Prev/Next list building in details page
  next.set("section", sectionValue);

  return `?${next.toString()}`;
};

const Profile = () => {
  const {
    selectedCountry,
    selectedCategory,
    selectedLanguage,
    selectedState,
    selectedCity,
  } = useOutletContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const sectionFromQuery = searchParams.get("section"); // ✅ persistent
  const sectionFromState = location.state?.fromSection; // ✅ only if no refresh
  const sectionKey = sectionFromState || sectionFromQuery;

  const categoryName = searchParams.get("category_name") || "";

  const isFeatured = searchParams.get("featured") === "1";
  const is_popular = searchParams.get("is_popular") === "1";

  const {
    item: people,
    ref,
    hasNextPage,
    isFetchingNextPage,
    isPending: peoplePending,
  } = useItemCategoryInfinite({
    key: "connect_people",
    endpoint: "people",
    listKey: "people", // ✅ API returns data.people
    category_name: categoryName,
    selectedCategory,
    selectedCountry,
    selectedLanguage,
    selectedState,
    selectedCity,
    enabled: true,
    threshold: 0.7,

    // ✅ if your hook supports featured param, pass it
    featured: sectionKey === "features" ? 1 : undefined,
  });

  const { data: featuredprofiledata = [] } = useQuery({
    queryKey: [
      "featuredprofiledata",
      selectedCountry,
      selectedCategory,
      selectedLanguage,
      selectedState,
      selectedCity,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedState) params.append("state", selectedState);
      if (selectedState) params.append("state", selectedState);
      if (sectionKey === "features") params.append("is_features", 1);
      if (sectionKey === "is_popular") params.append("is_popular", 1);

      const res = await axios.get(
        `${saffronURL}/api/v1/public/get-feature-people?${params.toString()}`,
      );
      return res.data;
    },
    retry: false,
    staleTime: 1000 * 60 * 60,
  });



  const hasPeople = (people?.length || 0) > 0;

  // ✅ Menu
  const { data: menuData, isLoading: loadingTree } = useQuery({
    queryKey: [
      "menuData_connect_slide",
      selectedCountry,
      selectedLanguage,
      selectedCategory,
      selectedState,
      selectedCity,
    ],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (selectedCountry) params.append("country", selectedCountry);
      if (selectedLanguage) params.append("language", selectedLanguage);
      if (selectedCategory) params.append("category", selectedCategory);
      if (selectedState) params.append("state", selectedState);
      if (selectedCity) params.append("city", selectedCity);

      const url = `${saffronURL}/api/v1/public/menu?${params.toString()}`;
      const resp = await axios.get(url);
      return resp.data;
    },
    enabled: !!(selectedCountry || selectedLanguage || selectedCategory),
    staleTime: 1000 * 60 * 10,
    retry: 2,
  });

  const navbaritems = useMemo(() => {
    return menuData?.data?.items?.find((item) => item.key === "profile");
  }, [menuData]);

const listWithPos = useMemo(() => {
  let sourceData;

  if (sectionKey === "features" || sectionKey === "is_popular") {
    sourceData = featuredprofiledata?.data;
  } else {
    sourceData = people;
  }

  return addPosition(sourceData);
}, [featuredprofiledata, people, sectionKey]);
  // ✅ Category change (same as Features)
  const handleCategorySelect = (child) => {
    const params = new URLSearchParams(searchParams);

    if (child?.code) params.set("category_name", child.code);
    else params.delete("category_name");

    // ✅ keep featured in URL (if you use it)
    if (isFeatured) params.set("featured", "1");
    if (is_popular) params.set("is_popular", "1");

    setSearchParams(params);
    window.scrollTo(0, 0);
  };

  // ✅ Navigate to details (same as Features)
  const handleOpenDetails = (item) => {
    const slug = item?.slug || item?.id;
    if (!slug) return;

    // ✅ Simple: section = "people"
    // (If you want featured vs normal separate prev/next, use:
    // section = isFeatured ? "featuredProfiles" : "people"
    const sectionValue = "people";

    navigate(
      {
        pathname: `${saffronRout}/details/people/${slug}`,
        search: buildSafeSearch(searchParams, sectionValue),
      },
      {
        state: {
          sectionKey: sectionValue,
          position: item.position ?? 0,
          breadcrumbTitle:
            item?.name || item?.title || item?.full_name || item?.slug,
        },
      },
    );

    window.scrollTo(0, 0);
  };

  const [path, setPath] = useState();

  return (
    <>
      <NavbarScroll
        items={navbaritems}
        activeCode={categoryName}
        onSelect={handleCategorySelect}
        loading={loadingTree}
        path={path}
        setPath={setPath}
      />
      <section className="py-3 px-1 md:px-5 lg:px-10">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {listWithPos?.map((item) => (
            <ConnectCard2
              key={item.id}
              data={item}
              onArticleClick={() => handleOpenDetails(item)}
            />
          ))}
        </div>

        {!peoplePending && !hasPeople && (
          <p className="text-gray-400 italic mt-4 text-center">
            No profiles found in <span className="font-medium">'Profile'</span>
          </p>
        )}

        {/* ✅ Infinite Scroll trigger */}
        {hasNextPage && <div ref={ref} className="h-10" />}

        {isFetchingNextPage && (
          <div className="text-center text-sm text-gray-500 py-4">
            Loading more...
          </div>
        )}
      </section>
    </>
  );
};

export default Profile;
